import React, { useEffect, useState } from 'react';
import {
    Input,
    Select,
    Option,
} from "@material-tailwind/react";
import {
    Plus,
    MessageSquare,
    AlertCircle,
    Paperclip,
    RefreshCw,
    Search,
    ArrowRight,
    Calendar,
} from "lucide-react";
import { SUPPORT_TICKET_STATUSES_ENUM } from '../../utils/enums';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetSupportTickets } from '../../custom-hooks/use-support';
import moment from 'moment';
import { SupportTicketCategoryIcon, SupportTicketPriorityConfig, SupportTicketStatusConfig } from '../../utils/ui-interpreters';
import { URLS } from '../../router-x/routes';
import { useAuth } from '../../services/contexts/authContext';

const PRIORITY_DOT = { low: 'bg-green-500', medium: 'bg-amber-500', high: 'bg-orange-500', urgent: 'bg-red-500' };

const CustomerSupportPortal = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: tickets, isLoading: isLoadingTickets, refetch: refetchTickets } = useGetSupportTickets();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [ownershipFilter, setOwnershipFilter] = useState('all');
    const [sortFilter, setSortFilter] = useState('updated_desc');

    useEffect(() => {
        if (location.state?.refreshTickets) {
            refetchTickets();
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state, location.pathname, navigate, refetchTickets]);

    const getTimeAgo = (date) => {
        return moment(date).fromNow();
    };

    const { userDetails } = useAuth();
    const baseUrl = userDetails?.role === 'team_member' ? URLS.TEAM_MEMBER.SUPPORT : URLS.CUSTOMER.SUPPORT;

    const handleViewTicket = (ticket) => {
        navigate(`${baseUrl}/${ticket._id}`);
    };

    const handleCreateTicket = () => {
        navigate(`${baseUrl}/create`);
    };

    const getFilteredTickets = () => {
        let filtered = tickets?.tickets || [];

        if (searchTerm) {
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket._id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.status === statusFilter);
        }

        if (ownershipFilter === 'mine') {
            filtered = filtered.filter(ticket => !ticket.createdBy || ticket.createdBy === userDetails?._id);
        } else if (ownershipFilter === 'team') {
            filtered = filtered.filter(ticket => ticket.createdBy && ticket.createdBy !== userDetails?._id);
        }

        if (sortFilter === 'updated_desc') {
            filtered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
        } else if (sortFilter === 'created_desc') {
            filtered.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        }

        return filtered;
    };

    const filteredTickets = getFilteredTickets();

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="font-display text-[25px] font-bold text-ink">Support Center</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Submit and track your support requests.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                    <button
                        onClick={() => refetchTickets()}
                        disabled={isLoadingTickets}
                        className="flex items-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-4 py-2.5 font-display text-[13px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream disabled:opacity-60"
                    >
                        <RefreshCw className={`h-4 w-4 ${isLoadingTickets ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={handleCreateTicket}
                        className="flex items-center gap-2 rounded-[11px] bg-brand px-4 py-2.5 font-display text-[13px] font-semibold text-white transition-colors hover:bg-brand-dark"
                    >
                        <Plus className="h-4 w-4" />
                        Create New Ticket
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-5 rounded-2xl border border-[#ECE3D5] bg-white p-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Input
                        type="text"
                        label="Ticket ID or Subject"
                        placeholder="Search tickets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        icon={<Search className="h-4 w-4" />}
                    />
                    <Select
                        label="Filter by Status"
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value)}
                    >
                        <Option value="all">All Statuses</Option>
                        {SUPPORT_TICKET_STATUSES_ENUM.map((status) => {
                            const statusConfig = SupportTicketStatusConfig(status);
                            return (
                                <Option key={status} value={status}>
                                    <div className="flex items-center gap-2">
                                        <statusConfig.icon className="h-4 w-4" />
                                        <span className="capitalize">{statusConfig?.label}</span>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                    {userDetails?.role === 'user' && (
                        <Select
                            label="Ownership"
                            value={ownershipFilter}
                            onChange={(value) => setOwnershipFilter(value)}
                        >
                            <Option value="all">All Tickets</Option>
                            <Option value="mine">My Tickets</Option>
                            <Option value="team">Team Member Tickets</Option>
                        </Select>
                    )}
                    <Select
                        label="Sort By"
                        value={sortFilter}
                        onChange={(value) => setSortFilter(value)}
                    >
                        <Option value="updated_desc">Recently Updated</Option>
                        <Option value="created_desc">Recently Added</Option>
                    </Select>
                </div>
                <p className="mt-4 text-sm font-medium text-[#8C8175]">
                    Showing {filteredTickets.length} of {tickets?.total || 0} tickets
                </p>
            </div>

            {/* Tickets List */}
            {isLoadingTickets ? (
                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-12 text-center">
                    <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin text-[#B7AC9E]" />
                    <p className="text-[#8C8175]">Loading your tickets...</p>
                </div>
            ) : filteredTickets.length === 0 ? (
                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cream-input text-brand">
                        <MessageSquare className="h-8 w-8" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-ink">No tickets found</h3>
                    <p className="mx-auto mb-6 mt-1 max-w-sm text-sm text-[#8C8175]">
                        {searchTerm || statusFilter !== 'all'
                            ? 'Try adjusting your search or filters'
                            : "You haven't submitted any support tickets yet"}
                    </p>
                    {!searchTerm && statusFilter === 'all' && (
                        <button
                            onClick={handleCreateTicket}
                            className="rounded-xl bg-brand px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                        >
                            Create Your First Ticket
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3.5">
                    {filteredTickets.map((ticket) => {
                        const statusConfig = SupportTicketStatusConfig(ticket.status);
                        const priorityConfig = SupportTicketPriorityConfig(ticket.priority);
                        const CategoryIcon = SupportTicketCategoryIcon(ticket.category);

                        return (
                            <div
                                key={ticket._id}
                                className="cursor-pointer rounded-2xl border border-[#ECE3D5] bg-white p-6 transition-all hover:-translate-y-px hover:border-[#E0C3A4] hover:shadow-[0_14px_30px_-20px_rgba(40,22,8,0.4)]"
                                onClick={() => handleViewTicket(ticket)}
                            >
                                <div className="mb-4 flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                                            <CategoryIcon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-display text-base font-semibold text-ink">{ticket.subject}</h3>
                                            <p className="text-xs font-medium uppercase tracking-wide text-[#A89C8C]">TKT-{ticket?._id.slice(-5)}</p>
                                        </div>
                                    </div>
                                    <ArrowRight className="h-5 w-5 shrink-0 text-[#C5B9AA]" />
                                </div>

                                <p className="mb-4 line-clamp-2 text-sm text-[#6B675F]">{ticket.description}</p>

                                <div className="mb-4 flex flex-wrap items-center gap-3">
                                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${statusConfig.color}`}>
                                        {statusConfig.label}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B675F]">
                                        <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[ticket.priority] || 'bg-amber-500'}`} />
                                        {priorityConfig.label}
                                    </span>
                                    <span className="text-sm capitalize text-[#8C8175]">
                                        {ticket?.category?.replace('_', ' ')}
                                    </span>
                                    {ticket.attachments.length > 0 && (
                                        <span className="flex items-center gap-1 text-sm text-[#8C8175]">
                                            <Paperclip className="h-4 w-4" />
                                            {ticket.attachments.length}
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center justify-between border-t border-[#F1E9DC] pt-4">
                                    <div className="flex items-center gap-4">
                                        {ticket.assignedAgent && (
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cream-input text-xs font-semibold text-brand">
                                                    {ticket.assignedAgent.name.charAt(0)}
                                                </div>
                                                <span className="text-sm text-[#6B675F]">{ticket.assignedAgent.name}</span>
                                            </div>
                                        )}
                                        {ticket.requiresCustomerAcknowledgment && (
                                            <span className="flex items-center gap-1 text-sm font-medium text-brand">
                                                <AlertCircle className="h-4 w-4" />
                                                Action Required
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-[#A89C8C]">
                                        <Calendar className="h-4 w-4" />
                                        {getTimeAgo(ticket.lastUpdated)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomerSupportPortal;
